import React from 'react';
import { Box, Container, Typography, Button, Card, TextField, MenuItem, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  Psychology as Brain, 
  SmartToy as Robot, 
  TrendingUp,
  SportsEsports as Gamepad2, 
  Visibility as Eye, 
  Language,
  Calculate as Calculator,
  Biotech as Science,
  Public as Globe,
  Check,
  Close,
  Phone,
  Email,
  LocationOn,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  Send,
  PlayArrow
} from '@mui/icons-material';

// Keyframe animations
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const logoSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const brainPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const orbit = keyframes`
  0% { transform: rotate(0deg) translateX(80px) rotate(0deg); }
  100% { transform: rotate(360deg) translateX(80px) rotate(-360deg); }
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components
const HeroSection = styled(Box)`
  min-height: calc(100vh - 80px);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #43e97b 100%);
  background-size: 400% 400%;
  animation: ${gradientShift} 10s ease infinite;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-top: 2rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
    z-index: 1;
  }
`;

const HeroContainer = styled(Container)`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const HeroBadge = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const HeroTitle = styled(Typography)`
  font-size: 3rem;
  font-weight: 800;
  color: white;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  
  span {
    background: linear-gradient(135deg, #fbbf24, #f59e0b, #d97706);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HeroDescription = styled(Typography)`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const HeroButtons = styled(Box)`
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1rem;
  text-transform: none;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4);
  }
`;

const SecondaryButton = styled(Button)`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1rem;
  text-transform: none;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.3);
  }
`;

const HeroStats = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const Stat = styled(Box)`
  text-align: left;
  
  h3 {
    font-size: 2rem;
    font-weight: 800;
    color: white;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
  }
`;

const FloatingCard = styled(Box)`
  position: relative;
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${float} 6s ease-in-out infinite;
  margin: 0 auto;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const AIBrain = styled(Box)`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  animation: ${brainPulse} 2s ease-in-out infinite;
  box-shadow: 0 0 30px rgba(102, 126, 234, 0.5);
`;

const FloatingElement = styled(Box)`
  position: absolute;
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  animation: ${orbit} 20s linear infinite;
  
  &.math { animation-delay: 0s; }
  &.science { animation-delay: -5s; }
  &.gk { animation-delay: -10s; }
  &.english { animation-delay: -15s; }
`;

const Section = styled(Box)`
  padding: 5rem 0;
  
  @media (max-width: 768px) {
    padding: 3rem 0;
  }
`;

const SectionHeader = styled(Box)`
  text-align: center;
  margin-bottom: 3rem;
  
  h2 {
    font-size: 2rem;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  
  p {
    font-size: 1rem;
    color: #475569;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
    font-weight: 500;
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 1.8rem;
    }
    
    p {
      font-size: 0.95rem;
    }
  }
`;

const FeatureCard = styled(Card)`
  height: 100%;
  background: white;
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  }
  
  h5 {
    color: #1e293b;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  
  p {
    color: #64748b;
    line-height: 1.6;
  }
`;

const FeatureIcon = styled(Box)`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.8rem;
  margin: 0 auto 1.5rem;
  transition: all 0.3s ease;
`;

const FeatureBadge = styled(Box)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
`;

const ProgramCard = styled(Card)`
  height: 100%;
  background: white;
  border-radius: 20px;
  padding: 0;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  position: relative;
  
  &.featured {
    border: 2px solid #667eea;
    transform: scale(1.05);
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
  
  h5 {
    color: #1e293b;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  
  p {
    color: #64748b;
    line-height: 1.6;
    margin-bottom: 1rem;
  }
`;



const AIBadge = styled(Box)`
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 0.3rem 0.6rem;
  border-radius: 15px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
  text-align: center;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
  align-self: flex-start;
  margin-top: 0.5rem;
`;



const CheckList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
  
  li {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
    color: #64748b;
    
    .MuiSvgIcon-root {
      color: #10b981;
      margin-right: 0.5rem;
      font-size: 1.2rem;
    }
  }
`;

const ProgramFooter = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
  
  span {
    background: #f1f5f9;
    color: #475569;
    padding: 0.25rem 0.75rem;
    border-radius: 50px;
    font-size: 0.8rem;
    font-weight: 600;
  }
`;

const StepsContainer = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const Step = styled(Box)`
  text-align: center;
  animation: ${slideInUp} 0.6s ease forwards;
  padding: 1rem;
  
  h5 {
    color: white;
    font-weight: 700;
    margin-bottom: 1rem;
    font-size: 1.3rem;
  }
  
  p {
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.6;
    font-size: 1rem;
  }
  
  &:nth-of-type(1) { animation-delay: 0.1s; }
  &:nth-of-type(2) { animation-delay: 0.2s; }
  &:nth-of-type(3) { animation-delay: 0.3s; }
  &:nth-of-type(4) { animation-delay: 0.4s; }
`;

const StepNumber = styled(Box)`
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 auto 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
`;

const PricingGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const PricingCard = styled(Card)`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
  position: relative;
  
  &.featured {
    border: 2px solid #667eea;
    transform: scale(1.05);
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const PricingBadge = styled(Box)`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const Price = styled(Box)`
  margin: 1rem 0;
  
  span {
    font-size: 3rem;
    font-weight: 800;
    color: #1e293b;
  }
  
  small {
    color: #64748b;
    font-size: 1rem;
  }
`;

const PricingButton = styled(Button)`
  width: 100%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 1rem;
  border-radius: 12px;
  font-weight: 700;
  text-transform: none;
  margin-top: 1.5rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
  }
`;

const ContactSection = styled(Section)`
  background: #f8fafc;
`;

const ContactContent = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  .MuiSvgIcon-root {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border-radius: 12px;
    padding: 12px;
  }
  
  h4 {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.25rem;
  }
  
  p {
    color: #64748b;
    font-size: 0.95rem;
  }
`;

const ContactForm = styled(Box)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled(Box)`
  margin-bottom: 1.5rem;
  
  .MuiTextField-root {
    width: 100%;
    
    .MuiOutlinedInput-root {
      border-radius: 12px;
    }
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 1rem;
  border-radius: 12px;
  font-weight: 700;
  text-transform: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
  }
`;

const Footer = styled(Box)`
  background: #1e293b;
  color: white;
  padding: 3rem 0 1rem;
`;

const FooterContent = styled(Box)`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterLogo = styled(Box)`
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  
  .MuiSvgIcon-root {
    margin-right: 0.5rem;
    font-size: 2rem;
    animation: ${logoSpin} 8s linear infinite;
  }
`;

const SocialLinks = styled(Box)`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  .MuiIconButton-root {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    transition: all 0.3s ease;
    
    &:hover {
      background: linear-gradient(135deg, #667eea, #764ba2);
      transform: translateY(-2px);
    }
  }
`;

const FooterSection = styled(Box)`
  h4 {
    font-size: 1.1rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  
  ul {
    list-style: none;
    padding: 0;
    
    li {
      margin-bottom: 0.5rem;
      
      a {
        color: rgba(255, 255, 255, 0.8);
        text-decoration: none;
        transition: color 0.3s ease;
        
        &:hover {
          color: white;
        }
      }
    }
  }
`;

const HomePage: React.FC = () => {
  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <HeroContainer maxWidth="lg">
          <Box>
            <HeroBadge>
              <Robot />
              <span>AI-Powered Adaptive Learning Platform</span>
            </HeroBadge>
            
            <HeroTitle variant="h1">
              Master Olympiad Skills with <span>AI-Powered Practice</span>
            </HeroTitle>
            
            <HeroDescription>
              Experience personalized learning with our adaptive testing system. AI-generated questions 
              adjust to your skill level in real-time across Math, Science, English, and General Knowledge 
              for students from Grade 1-12.
            </HeroDescription>
            
            <HeroButtons>
              <PrimaryButton startIcon={<Brain />}>
                Try AI Adaptive Test
              </PrimaryButton>
              <SecondaryButton startIcon={<PlayArrow />}>
                Watch Demo
              </SecondaryButton>
            </HeroButtons>
            
            <HeroStats>
              <Stat>
                <Typography variant="h3">4</Typography>
                <Typography>Core Subjects</Typography>
              </Stat>
              <Stat>
                <Typography variant="h3">400+</Typography>
                <Typography>AI-Generated Questions</Typography>
              </Stat>
              <Stat>
                <Typography variant="h3">12</Typography>
                <Typography>Grade Levels</Typography>
              </Stat>
            </HeroStats>
          </Box>
          
          <Box>
            <FloatingCard>
              <AIBrain>
                <Brain />
              </AIBrain>
              <FloatingElement className="math">
                <Calculator />
              </FloatingElement>
              <FloatingElement className="science">
                <Science />
              </FloatingElement>
              <FloatingElement className="gk">
                <Globe />
              </FloatingElement>
              <FloatingElement className="english">
                <Language />
              </FloatingElement>
            </FloatingCard>
          </Box>
        </HeroContainer>
      </HeroSection>

      {/* AI Features Section */}
      <Section sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Container maxWidth="lg">
          <SectionHeader sx={{ 
            '& h2': { color: 'white' }, 
            '& p': { color: 'rgba(255, 255, 255, 0.9)' } 
          }}>
            <Typography variant="h2">Smart Learning Features</Typography>
            <Typography>Discover how our AI-powered platform personalizes your learning experience</Typography>
          </SectionHeader>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
            gap: 3 
          }}>
            <FeatureCard>
              <FeatureIcon>
                <Brain />
              </FeatureIcon>
              <Typography variant="h5" gutterBottom fontWeight={700}>
                Adaptive Difficulty
              </Typography>
              <Typography color="text.secondary" paragraph>
                Questions automatically adjust difficulty based on your performance, keeping you challenged but not overwhelmed.
              </Typography>
              <FeatureBadge>Smart</FeatureBadge>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <Robot />
              </FeatureIcon>
              <Typography variant="h5" gutterBottom fontWeight={700}>
                AI Question Generation
              </Typography>
              <Typography color="text.secondary" paragraph>
                OpenAI-powered system generates unlimited practice questions tailored to your grade level and subject.
              </Typography>
              <FeatureBadge>Unlimited</FeatureBadge>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <TrendingUp />
              </FeatureIcon>
              <Typography variant="h5" gutterBottom fontWeight={700}>
                Progress Analytics
              </Typography>
              <Typography color="text.secondary" paragraph>
                Track your performance across subjects with detailed insights on strengths, weaknesses, and improvement areas.
              </Typography>
              <FeatureBadge>Insights</FeatureBadge>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <Gamepad2 />
              </FeatureIcon>
              <Typography variant="h5" gutterBottom fontWeight={700}>
                Interactive Learning
              </Typography>
              <Typography color="text.secondary" paragraph>
                Engaging test interface with instant explanations and step-by-step solutions for every question.
              </Typography>
              <FeatureBadge>Engaging</FeatureBadge>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <Eye />
              </FeatureIcon>
              <Typography variant="h5" gutterBottom fontWeight={700}>
                Session Management
              </Typography>
              <Typography color="text.secondary" paragraph>
                Pause and resume tests anytime, with comprehensive session history and performance tracking.
              </Typography>
              <FeatureBadge>Flexible</FeatureBadge>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <Language />
              </FeatureIcon>
              <Typography variant="h5" gutterBottom fontWeight={700}>
                Multi-Subject Support
              </Typography>
              <Typography color="text.secondary" paragraph>
                Practice across Mathematics, Science, English, and General Knowledge with grade-specific content.
              </Typography>
              <FeatureBadge>Comprehensive</FeatureBadge>
            </FeatureCard>
          </Box>
        </Container>
      </Section>

      {/* Programs Section */}
      <Section sx={{ background: '#f8fafc' }}>
        <Container maxWidth="lg">
          <SectionHeader>
            <Typography variant="h2">Subject Areas</Typography>
            <Typography>Master key academic subjects with our adaptive learning system</Typography>
          </SectionHeader>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, 
            gap: 3,
            alignItems: 'stretch',
            '& > *': { minWidth: '280px' }
          }}>
                        <ProgramCard>
                {/* Header Section */}
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #667eea, #764ba2)', 
                  color: 'white', 
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
              color: 'white',
                      fontSize: '1.5rem'
                    }}>
                      <Calculator />
                    </Box>
                    <Typography variant="h5" fontWeight={700} color="white">
                      Mathematics
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <AIBadge>AI-Enhanced</AIBadge>
        </Box>
      </Box>

                {/* Content Section */}
                <Box sx={{ p: 2 }}>
                  <Typography color="text.secondary" paragraph>
                    Build strong mathematical foundations with adaptive questions covering arithmetic, algebra, geometry, and problem-solving.
                  </Typography>
                  <CheckList>
                    <li><Check /> Grade-specific content</li>
                    <li><Check /> Adaptive difficulty adjustment</li>
                    <li><Check /> Detailed explanations</li>
                  </CheckList>
                  <ProgramFooter>
                    <span>60 minutes</span>
                    <span>Grades 1-12</span>
                  </ProgramFooter>
                </Box>
              </ProgramCard>
            
                        <ProgramCard>
                {/* Header Section */}
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #f093fb, #f5576c)', 
                  color: 'white', 
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
            color: 'white',
                      fontSize: '1.5rem'
                    }}>
                      <Science />
                    </Box>
                    <Typography variant="h5" fontWeight={700} color="white">
                      Science
        </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <AIBadge>VR Ready</AIBadge>
                  </Box>
                </Box>
                
                {/* Content Section */}
                <Box sx={{ p: 2 }}>
                  <Typography color="text.secondary" paragraph>
                    Discover scientific concepts through comprehensive questions covering physics, chemistry, biology, and earth sciences.
                  </Typography>
                  <CheckList>
                    <li><Check /> Multi-branch science coverage</li>
                    <li><Check /> Concept-based learning</li>
                    <li><Check /> Practical applications</li>
                  </CheckList>
                  <ProgramFooter>
                    <span>60 minutes</span>
                    <span>Grades 1-12</span>
                  </ProgramFooter>
                </Box>
              </ProgramCard>
            
                        <ProgramCard>
                {/* Header Section */}
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #4facfe, #00f2fe)', 
                  color: 'white', 
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.5rem'
                    }}>
                      <Globe />
                    </Box>
                    <Typography variant="h5" fontWeight={700} color="white">
                      General Knowledge
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <AIBadge>Live Updates</AIBadge>
                  </Box>
                </Box>
                
                {/* Content Section */}
                <Box sx={{ p: 2 }}>
                  <Typography color="text.secondary" paragraph>
                    Expand your knowledge with questions covering history, geography, current affairs, and general awareness topics.
                  </Typography>
                  <CheckList>
                    <li><Check /> Wide topic coverage</li>
                    <li><Check /> Current affairs integration</li>
                    <li><Check /> Cultural awareness</li>
                  </CheckList>
                  <ProgramFooter>
                    <span>60 minutes</span>
                    <span>Grades 1-12</span>
                  </ProgramFooter>
      </Box>
              </ProgramCard>
            
                        <ProgramCard>
                {/* Header Section */}
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #43e97b, #38f9d7)', 
            color: 'white',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.5rem'
                    }}>
                      <Language />
                    </Box>
                    <Typography variant="h5" fontWeight={700} color="white">
                      English
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <AIBadge>NLP Powered</AIBadge>
                  </Box>
                </Box>
                
                {/* Content Section */}
                <Box sx={{ p: 2 }}>
                  <Typography color="text.secondary" paragraph>
                    Strengthen language skills with questions on grammar, vocabulary, comprehension, and literature.
                  </Typography>
                  <CheckList>
                    <li><Check /> Grammar and vocabulary</li>
                    <li><Check /> Reading comprehension</li>
                    <li><Check /> Language fundamentals</li>
                  </CheckList>
                  <ProgramFooter>
                    <span>60 minutes</span>
                    <span>Grades 1-12</span>
                  </ProgramFooter>
                </Box>
              </ProgramCard>
          </Box>
        </Container>
      </Section>

      {/* How It Works Section */}
      <Section sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Container maxWidth="lg">
          <SectionHeader sx={{ 
            '& h2': { color: 'white' }, 
            '& p': { color: 'rgba(255, 255, 255, 0.9)' } 
          }}>
            <Typography variant="h2">How Adaptive Learning Works</Typography>
            <Typography>Experience personalized learning that adapts to your skill level</Typography>
          </SectionHeader>
          
          <StepsContainer>
            <Step>
              <StepNumber>1</StepNumber>
              <Typography variant="h5" gutterBottom fontWeight={700}>
                Choose Subject & Grade
              </Typography>
              <Typography color="text.secondary">
                Select your preferred subject (Math, Science, English, or GK) and grade level to start your adaptive test.
              </Typography>
            </Step>
            
            <Step>
              <StepNumber>2</StepNumber>
              <Typography variant="h5" gutterBottom fontWeight={700}>
                Start Adaptive Test
              </Typography>
              <Typography color="text.secondary">
                Begin with questions at your grade level. The system tracks your performance to adjust difficulty.
              </Typography>
            </Step>
            
            <Step>
              <StepNumber>3</StepNumber>
              <Typography variant="h5" gutterBottom fontWeight={700}>
                AI Adjusts Difficulty
              </Typography>
              <Typography color="text.secondary">
                Questions become harder or easier based on your answers, keeping you in the optimal learning zone.
              </Typography>
            </Step>
            
            <Step>
              <StepNumber>4</StepNumber>
              <Typography variant="h5" gutterBottom fontWeight={700}>
                Review & Learn
              </Typography>
              <Typography color="text.secondary">
                Get detailed explanations for each question and track your progress through comprehensive analytics.
              </Typography>
            </Step>
          </StepsContainer>
        </Container>
      </Section>

      {/* Pricing Section */}
      <Section sx={{ background: '#f8fafc' }}>
        <Container maxWidth="lg">
          <SectionHeader>
            <Typography variant="h2">Simple, Affordable Pricing</Typography>
            <Typography>Start free and upgrade when you're ready for unlimited practice</Typography>
          </SectionHeader>
          
          <PricingGrid>
            <PricingCard>
              <Typography variant="h4" gutterBottom fontWeight={700}>Free Plan</Typography>
              <Price>
                <span>₹0</span>
                <small>/forever</small>
              </Price>
              <CheckList>
                <li><Check /> 1 free test per subject</li>
                <li><Check /> Basic performance tracking</li>
                <li><Check /> Access to all 4 subjects</li>
                <li><Check /> Grade-specific questions</li>
                <li><Close sx={{ color: '#ef4444' }} /> Unlimited tests</li>
              </CheckList>
              <PricingButton>Start Free</PricingButton>
            </PricingCard>
            
            <PricingCard className="featured">
              <PricingBadge>Most Popular</PricingBadge>
              <Typography variant="h4" gutterBottom fontWeight={700}>Monthly Plan</Typography>
              <Price>
                <span>₹200</span>
                <small>/month</small>
              </Price>
              <CheckList>
                <li><Check /> Unlimited adaptive tests</li>
                <li><Check /> AI-generated questions</li>
                <li><Check /> Detailed progress analytics</li>
                <li><Check /> Session history & review</li>
                <li><Check /> All subjects & grades</li>
              </CheckList>
              <PricingButton>Subscribe Monthly</PricingButton>
            </PricingCard>
            
            <PricingCard>
              <Typography variant="h4" gutterBottom fontWeight={700}>Yearly Plan</Typography>
              <Price>
                <span>₹2,000</span>
                <small>/year</small>
              </Price>
              <CheckList>
                <li><Check /> Everything in Monthly</li>
                <li><Check /> Save ₹400 annually</li>
                <li><Check /> Priority support</li>
                <li><Check /> Advanced analytics</li>
                <li><Check /> One-time payment</li>
              </CheckList>
              <PricingButton>Subscribe Yearly</PricingButton>
            </PricingCard>
          </PricingGrid>
        </Container>
      </Section>

      {/* Contact Section */}
      <ContactSection>
        <Container maxWidth="lg">
          <ContactContent>
            <Box>
              <Typography variant="h3" gutterBottom fontWeight={800} color="primary">
                Ready to Start Learning?
              </Typography>
              <Typography variant="h6" paragraph color="text.secondary">
                Join students across India who are improving their academic skills with our adaptive learning platform.
              </Typography>
              
              <Box mt={3}>
                <ContactItem>
                  <LocationOn />
                  <Box>
                    <Typography variant="h6" fontWeight={700}>Location</Typography>
                    <Typography color="text.secondary">Ahmedabad, Gujarat, India</Typography>
                  </Box>
                </ContactItem>
                
                <ContactItem>
                  <Phone />
                  <Box>
                    <Typography variant="h6" fontWeight={700}>Phone</Typography>
                    <Typography color="text.secondary">+91 12345 67890</Typography>
                  </Box>
                </ContactItem>
                
                <ContactItem>
                  <Email />
                  <Box>
                    <Typography variant="h6" fontWeight={700}>Email</Typography>
                    <Typography color="text.secondary">info@apexscholarleague.com</Typography>
                  </Box>
                </ContactItem>
              </Box>
            </Box>
            
            <ContactForm>
              <FormGroup>
                <TextField
                  fullWidth
                  placeholder="Your Name"
                  variant="outlined"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <TextField
                  fullWidth
                  placeholder="Your Email"
                  type="email"
                  variant="outlined"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <TextField
                  fullWidth
                  select
                  placeholder="Select Grade"
                  variant="outlined"
                  required
                >
                  <MenuItem value="1-5">Grades 1-5</MenuItem>
                  <MenuItem value="6-8">Grades 6-8</MenuItem>
                  <MenuItem value="9-12">Grades 9-12</MenuItem>
                  <MenuItem value="school">School Inquiry</MenuItem>
                </TextField>
              </FormGroup>
              
              <FormGroup>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Your Message"
                  variant="outlined"
                />
              </FormGroup>
              
              <SubmitButton startIcon={<Send />}>
                Send Message
              </SubmitButton>
            </ContactForm>
          </ContactContent>
        </Container>
      </ContactSection>

      {/* Footer */}
      <Footer>
        <Container maxWidth="lg">
          <FooterContent>
            <Box>
              <FooterLogo>
                <Brain />
                <span>Apex Scholar League</span>
              </FooterLogo>
              <Typography color="rgba(255, 255, 255, 0.8)" paragraph>
                Empowering the next generation with AI-powered competitive learning experiences.
              </Typography>
              <SocialLinks>
                <IconButton>
                  <Facebook />
                </IconButton>
                <IconButton>
                  <Twitter />
                </IconButton>
                <IconButton>
                  <LinkedIn />
                </IconButton>
                <IconButton>
                  <Instagram />
                </IconButton>
              </SocialLinks>
            </Box>
            
            <FooterSection>
              <Typography variant="h6" fontWeight={700}>Quick Links</Typography>
              <ul>
                <li><a href="#ai-features">AI Features</a></li>
                <li><a href="#programs">Programs</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </FooterSection>
            
            <FooterSection>
              <Typography variant="h6" fontWeight={700}>Programs</Typography>
              <ul>
                <li><a href="/test?subject=math">Mathematics</a></li>
                <li><a href="/test?subject=science">Science</a></li>
                <li><a href="/test?subject=gk">General Knowledge</a></li>
                <li><a href="/test?subject=english">English</a></li>
              </ul>
            </FooterSection>
          </FooterContent>
    </Container>
      </Footer>
    </Box>
  );
};

export default HomePage; 